/**
 * contextMemoryEngine.js
 *
 * Unified memory access layer. Provides a single interface to all persistent
 * context sources in Kevia — entity memory, workflow state, session continuity.
 *
 * Also tracks recurrent conversation topics in-session (no cross-session
 * persistence yet — that requires a separate storage design).
 *
 * Public API:
 *   getContextMemory(conversationId)  → string|null (compact memory summary)
 *   trackTopic(conversationId, topic) → void
 *   getTopics(conversationId)         → string[]
 *   clearMemory(conversationId)       → void
 */

import { getTrackedEntity }   from "./conversationEntityTracker";
import { getState }           from "../kevia-runtime/runtimeStore";
import { isSessionSaturated } from "../kevia-runtime/humanRhythm";

// In-session topic tracker — { [conversationId]: string[] }
const _topicsByConversation = new Map();
const MAX_TOPICS = 5;

/**
 * Track a recurrent topic for a conversation.
 * Topics are extracted from intent/entity combinations, not raw text.
 *
 * @param {string} conversationId
 * @param {string} topic  — e.g. "seguimiento_familias", "planificación_semana"
 */
export function trackTopic(conversationId, topic) {
  if (!conversationId || !topic) return;
  const existing = _topicsByConversation.get(conversationId) ?? [];
  if (!existing.includes(topic)) {
    const next = [topic, ...existing].slice(0, MAX_TOPICS);
    _topicsByConversation.set(conversationId, next);
  }
}

/**
 * Get recurrent topics for a conversation (most recent first).
 *
 * @param {string} conversationId
 * @returns {string[]}
 */
export function getTopics(conversationId) {
  return _topicsByConversation.get(conversationId) ?? [];
}

/**
 * Build a compact context memory string for a conversation.
 * Returns null if nothing meaningful to surface.
 *
 * @param {string} conversationId
 * @returns {string|null}
 */
export function getContextMemory(conversationId) {
  if (isSessionSaturated()) return null;

  const parts = [];

  // Last tracked entity from this conversation
  const entity = getTrackedEntity(conversationId);
  if (entity?.name && entity?.type) {
    const typeLabel = { person: "Persona", family: "Familia", church: "Iglesia", event: "Evento" }[entity.type] ?? entity.type;
    parts.push(`Entidad activa: ${entity.name} (${typeLabel})`);
  }

  // Recurrent topics (max 2 most recent)
  const topics = getTopics(conversationId).slice(0, 2);
  if (topics.length > 0) {
    parts.push(`Temas recientes: ${topics.join(", ")}`);
  }

  // Current module context
  const rtState = getState();
  if (rtState?.currentModule && rtState.currentModule !== "inicio") {
    parts.push(`Módulo actual: ${rtState.currentModule}`);
  }

  return parts.length > 0 ? parts.join(" | ") : null;
}

/**
 * Clear all in-session memory for a conversation.
 * Called when a conversation is deleted.
 *
 * @param {string} conversationId
 */
export function clearMemory(conversationId) {
  _topicsByConversation.delete(conversationId);
}
